/**
 * Console log writer — fire-and-forget insert into `cortex_events_log`.
 *
 * The Mycelium backbone calls this from its post-publish hook (see
 * `mycelium-event-backbone.service.ts`). The brain edge function and the
 * cortex SSE route call it directly with their own service-role client.
 *
 * Insert failures are swallowed and logged; the console is observability,
 * never the source of truth.
 */

import { SupabaseClient } from "@supabase/supabase-js";
import type {
  CortexEventLogInsert,
  CortexEventLogSource,
} from "@flowstate/types";
import type { MyceliumEvent } from "@flowstate/mycelium-core";
import { logger } from "../../lib/logger";
import {
  CONSOLE_DOMAIN_ALLOWLIST,
  inferStatus,
  summarizeEvent,
} from "./summarize-event";

const CONSOLE_TABLE = "cortex_events_log";

export interface ConsoleLogContext {
  /** Required — log rows are scoped to an organization. */
  orgId?: string;
  userId?: string;
  principalId?: string;
  cortexSessionId?: string;
  cortexRunId?: string;
  correlationId?: string;
  causationId?: string;
  durationMs?: number;
  source?: CortexEventLogSource;
}

/**
 * Translate a Mycelium event + caller context into a row payload, dropping
 * events whose domain isn't in the allowlist.
 */
export function buildConsoleRow(
  event: Pick<MyceliumEvent, "id" | "domain" | "type" | "payload" | "metadata"> & {
    timestamp?: number;
  },
  ctx: ConsoleLogContext,
): CortexEventLogInsert | null {
  if (!CONSOLE_DOMAIN_ALLOWLIST.has(event.domain)) return null;
  if (!ctx.orgId) return null;

  const payload = (event.payload ?? {}) as Record<string, unknown>;
  const occurredAtMs = event.timestamp ?? Date.now();

  return {
    event_id: event.id,
    domain: event.domain,
    event_type: event.type,
    org_id: ctx.orgId,
    user_id: ctx.userId ?? null,
    principal_id: ctx.principalId ?? null,
    cortex_session_id: ctx.cortexSessionId ?? null,
    cortex_run_id: ctx.cortexRunId ?? null,
    correlation_id: ctx.correlationId ?? null,
    causation_id: ctx.causationId ?? null,
    priority: typeof event.metadata?.priority === "number" ? event.metadata.priority : 5,
    source: ctx.source ?? "backbone",
    summary: summarizeEvent({
      domain: event.domain,
      type: event.type,
      payload,
      durationMs: ctx.durationMs,
      source: ctx.source,
    }),
    status: inferStatus(event.type),
    duration_ms: ctx.durationMs ?? null,
    payload,
    metadata: (event.metadata ?? {}) as Record<string, unknown>,
    occurred_at: new Date(occurredAtMs).toISOString(),
  };
}

/**
 * Insert a single row. Never throws; logs on failure.
 */
export async function writeConsoleRow(
  client: SupabaseClient,
  row: CortexEventLogInsert,
): Promise<void> {
  try {
    const { error } = await client.from(CONSOLE_TABLE).insert(row);
    if (error) {
      logger.debug("[console-log] insert failed", {
        message: error.message,
        domain: row.domain,
        type: row.event_type,
      });
    }
  } catch (err) {
    logger.debug("[console-log] insert threw", {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Convenience: build + write in one call. Resolves once the insert returns.
 * Callers should NOT `await` this from a hot path — fire-and-forget.
 */
export async function logEventToConsole(
  client: SupabaseClient,
  event: Parameters<typeof buildConsoleRow>[0],
  ctx: ConsoleLogContext,
): Promise<void> {
  const row = buildConsoleRow(event, ctx);
  if (!row) return;
  await writeConsoleRow(client, row);
}
